import { useState } from "react";
import { useTranslation } from "react-i18next";
import Tabs from "@/components/controls/Tabs";
import { ExpandView, Schedule } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import { useInfiniteScrollTrigger } from "@/hooks/useInfiniteScrollTrigger";
import { useSavedReportsInfinite } from "@/entities/savedReports/hooks";
import { ReportRow } from "@/components/global/Chat/components/dreamerReports/ReportRow";
import { ReportPreviewHost } from "@/components/global/Chat/components/dreamerReports/ReportPreviewHost";
import { ReportRowModals } from "@/components/global/Chat/components/dreamerReports/ReportRowModals";
import { useReportSurfaceTargets } from "@/components/global/Chat/components/dreamerReports/useReportSurfaceTargets";
import { ExpandedReportsModal } from "@/components/global/Chat/components/dreamerReports/ExpandedReportsModal";
import { ReportsEmptyState } from "@/components/global/Chat/components/dreamerReports/ReportsEmptyState";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";

const ALL_REPORTS_FILTER = "all";
const SCHEDULED_FILTER = "scheduled";

export const ReportsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState(ALL_REPORTS_FILTER);
  const [isExpandedOpen, setIsExpandedOpen] = useState(false);
  const { rowHandlers, previewHostProps, rowModalsProps } =
    useReportSurfaceTargets();

  const {
    reports,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useSavedReportsInfinite({
    scheduledOnly: selectedFilter === SCHEDULED_FILTER
  });

  const { rootRef, sentinelRef } = useInfiniteScrollTrigger({
    canLoadMore: !!hasNextPage && !isFetchingNextPage && !error,
    onLoadMore: fetchNextPage,
    itemCount: reports.length
  });

  return (
    <div className="flex h-full min-h-0 flex-col" data-test="reports-panel">
      <div className="flex h-16 shrink-0 items-center justify-between gap-2 px-4">
        {/* Styled as the activities tabs on a record page. */}
        <Tabs
          options={{
            [ALL_REPORTS_FILTER]: t(`${TRANSLATION_KEY}.filters.all`),
            [SCHEDULED_FILTER]: (
              <span className="flex items-center gap-1.5">
                {/* The active trigger recolours its text, so the icon keeps its own. */}
                <IconContainer
                  as="span"
                  className="size-[18px]"
                  color="primary"
                >
                  <Schedule />
                </IconContainer>
                {t(`${TRANSLATION_KEY}.filters.scheduled`)}
              </span>
            )
          }}
          value={selectedFilter}
          valueSelected={setSelectedFilter}
          dataTest="reports-filter-tabs"
          variant="pill"
          size="lg"
        />
        <IconButton
          size="lg"
          onClick={() => setIsExpandedOpen(true)}
          aria-label={t(`${TRANSLATION_KEY}.expand`)}
          data-test="reports-expand"
        >
          <ExpandView />
        </IconButton>
      </div>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : reports.length === 0 && !error ? (
        <ReportsEmptyState
          scheduledOnly={selectedFilter === SCHEDULED_FILTER}
        />
      ) : reports.length === 0 ? (
        <Text
          as="div"
          variant="bodySecondary"
          className="h-full w-full content-center self-center text-center"
        >
          {t(`${TRANSLATION_KEY}.loadFailed`)}
        </Text>
      ) : (
        <div ref={rootRef} className="min-h-0 flex-1 overflow-y-auto">
          <Text as="div" variant="smLabelSecondary" className="px-4 py-2">
            {t(`${TRANSLATION_KEY}.nameHeader`)}
          </Text>
          {reports.map((report) => (
            <ReportRow
              key={report.id}
              report={report}
              {...rowHandlers}
            />
          ))}
          <div ref={sentinelRef} className="h-px" />
          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Spinner />
            </div>
          )}
        </div>
      )}
      <ExpandedReportsModal
        isOpen={isExpandedOpen}
        setIsOpen={setIsExpandedOpen}
        scheduledOnly={selectedFilter === SCHEDULED_FILTER}
      />
      <ReportPreviewHost {...previewHostProps} />
      <ReportRowModals {...rowModalsProps} />
    </div>
  );
};
